import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6ipr1mfjdhm2lq4sua0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-[#0A0A0A] rounded-t-[32px]">
      <div className="max-w-[1440px] mx-auto px-20">
        <div className="grid grid-cols-3 gap-16 mb-16">
          {/* Left Column - Form */}
          <div className="col-span-2">
            <h3 className="text-5xl font-light text-white mb-6">GET IN TOUCH</h3>
            <p className="text-base text-[#999999] leading-relaxed mb-12 max-w-xl">
              Have questions about our products or want to learn more about what we do? We'd love to hear from you.
            </p>

            <form 
              id="contact-form" 
              onSubmit={handleSubmit} 
              data-readdy-form
              className="space-y-6"
            >
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your Name"
                    required
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#666666] focus:outline-none focus:border-[#4A9FFF] transition-colors text-sm"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Your Email"
                    required
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#666666] focus:outline-none focus:border-[#4A9FFF] transition-colors text-sm"
                  />
                </div>
              </div>
              <div>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Subject"
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#666666] focus:outline-none focus:border-[#4A9FFF] transition-colors text-sm"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  placeholder="Your Message"
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#666666] focus:outline-none focus:border-[#4A9FFF] transition-colors resize-none text-sm"
                ></textarea>
                <div className="text-xs text-[#666666] mt-2 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-10 py-4 bg-[#4A9FFF] text-white font-semibold rounded-lg hover:bg-[#3A8FEF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-sm mt-4">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm mt-4">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>

          {/* Right Column - Contact Info */}
          <div className="space-y-12">
            {/* Contact Details */}
            <div>
              <div className="uppercase text-sm font-semibold tracking-[2px] text-[#666666] mb-6">
                CONTACT
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-[#999999]"></i>
                  </div>
                  <a 
                    href="mailto:help@frenkidoo.com" 
                    className="text-base text-white hover:text-[#4A9FFF] transition-colors cursor-pointer"
                  >
                    help@frenkidoo.com
                  </a>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-lg text-[#999999]"></i>
                  </div>
                  <a 
                    href="tel:+12513468679" 
                    className="text-base text-white hover:text-[#4A9FFF] transition-colors cursor-pointer"
                  >
                    +1 2513468679
                  </a>
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <div className="uppercase text-sm font-semibold tracking-[2px] text-[#666666] mb-6">
                LOCATION
              </div>
              <div className="text-base text-white leading-relaxed space-y-1">
                <p>GOCE DELCHEV 58</p>
                <p>STRUMICA</p>
                <p>2400</p>
                <p>North Macedonia</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-12 border-t border-[#333333]">
          <div className="text-center">
            <p className="text-sm text-[#666666]">
              © 2025 FRENKI D.O.O. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}